import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Chip,
  Switch,
  Tooltip,
  Snackbar,
  Alert,
  Container,
  FormControlLabel,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { Phone } from '@mui/icons-material';
import LinkIcon from '@mui/icons-material/Link';
import EditAccount from './EditAccount';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { db, auth } from '../firebaseConfig';
import { doc, updateDoc, onSnapshot, getDoc } from 'firebase/firestore';
import PeopleIcon from '@mui/icons-material/People';
import CloseIcon from '@mui/icons-material/Close';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDocs, collection } from 'firebase/firestore';
import ChatIcon from '@mui/icons-material/Chat';
import TimerIcon from '@mui/icons-material/Timer';
import { getMessaging, getToken } from 'firebase/messaging';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [editAccountOpen, setEditAccountOpen] = useState(false);
  const [isInSidebar, setIsInSidebar] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedResponder, setSelectedResponder] = useState(null);
  const [editForm, setEditForm] = useState({
    displayName: '',
    unitNumber: '',
    cellNumber: '',
    certLevel: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAvailability = async () => {
      if (!user) {
        setIsAvailable(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setIsAvailable(userData.available || false);
          setIsAdmin(userData.isAdmin || false);
        }
      } catch (error) {
        console.error('Error fetching availability:', error);
      }
    };

    fetchAvailability();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        setIsAvailable(userData.available || false);
      }
    });

    return () => unsubscribe();
  }, [user]);

  const handleAvailabilityChange = async (event) => {
    if (!user) return;
    
    const newAvailability = event.target.checked;
    setIsAvailable(newAvailability); // Update local state immediately for better UX
    
    try {
      setIsLoading(true);
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        available: newAvailability,
        lastStatusUpdate: new Date().toISOString()
      });

      // Call Cloud Function to send notifications
      const sendAvailabilityNotification = httpsCallable(functions, 'sendAvailabilityNotification');
      await sendAvailabilityNotification({
        userId: user.uid,
        displayName: user.displayName || user.email.split('@')[0],
        available: newAvailability
      });

      // Show local notification
      if (Notification.permission === 'granted') {
        new Notification(
          `${user.displayName || user.email.split('@')[0]} is now ${newAvailability ? 'available' : 'unavailable'}`
        );
      }
    } catch (error) {
      console.error('Error updating availability:', error);
      // Revert the switch state if the update fails
      setIsAvailable(!newAvailability);
      setSnackbar({
        open: true,
        message: 'Failed to update availability status',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize FCM token
  useEffect(() => {
    const initializeFCM = async () => {
      if (!user) return;

      try {
        const messaging = getMessaging();
        const token = await getToken(messaging, {
          vapidKey: 'YOUR_VAPID_KEY' // Replace with your VAPID key
        });

        if (token) {
          // Store the token in the user's document
          const userDocRef = doc(db, 'users', user.uid);
          await updateDoc(userDocRef, {
            fcmToken: token
          });
        }
      } catch (error) {
        console.error('Error initializing FCM:', error);
      }
    };

    initializeFCM();
  }, [user]);

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const handleProfileMenuToggle = () => {
    setProfileMenuOpen(!profileMenuOpen);
  };

  const handlePasswordDialogOpen = () => {
    setPasswordDialogOpen(true);
    setProfileMenuOpen(false);
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleChangePassword = () => {
    if (newPassword === confirmPassword) {
      // Call your API to change the password
      console.log('Password changed successfully');
      handlePasswordDialogClose();
    } else {
      alert('Passwords do not match!');
    }
  };

  const handleEditAccountOpen = () => {
    setEditAccountOpen(true);
    setProfileMenuOpen(false);
  };

  const handleEditAccountClose = () => {
    setEditAccountOpen(false);
  };

  const navItems = [
    { text: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
    { text: 'Hospitals', path: '/ImportantInfo', icon: <LocalHospitalIcon /> },
    { text: 'Reports', path: '/reports', icon: <AssessmentIcon /> },
    { text: 'Directory', path: '/directory', icon: <PeopleIcon /> },
    { text: 'Admin', path: '/admin', icon: <AdminPanelSettingsIcon /> },
    { text: 'Resources', path: '/resources', icon: <LinkIcon /> },
    { text: 'Expiration List', path: '/expiration-list', icon: <TimerIcon /> },
  ];

  const renderNavItems = () => (
    <List sx={{ 
      width: '100%',
      maxWidth: 360,
      bgcolor: 'background.paper',
      '& .MuiListItem-root': {
        borderRadius: '8px',
        margin: '4px 8px',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
        },
        '&.Mui-selected': {
          backgroundColor: 'primary.main',
          color: 'white',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
        },
      },
    }}>
      {navItems.map((item) => (
        <ListItem
          button
          key={item.text}
          component={Link}
          to={item.path}
          onClick={isMobile ? toggleDrawer(false) : undefined}
          selected={location.pathname === item.path}
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
              {item.icon}
            </Box>
            <ListItemText 
              primary={item.text} 
              sx={{ 
                '& .MuiTypography-root': {
                  fontSize: '1rem',
                  fontWeight: '500',
                }
              }}
            />
          </Box>
        </ListItem>
      ))}
    </List>
  );

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: '#1976d2' }}>
        <Container maxWidth="xl" sx={{ 
          width: '100%', 
          maxWidth: '100%', 
          overflow: 'hidden',
          boxSizing: 'border-box',
          px: 1
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            py: 1,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            {/* User and Availability Row */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              alignItems: 'center',
              mb: 1,
              gap: 2
            }}>
              {user && (
                <>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 1,
                    px: 2,
                    py: 0.5
                  }}>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      {user.displayName || user.email.split('@')[0]}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isAvailable}
                          onChange={handleAvailabilityChange}
                          disabled={isLoading}
                          color="success"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          {isAvailable ? 'Available' : 'Unavailable'}
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                  </Box>
                  <IconButton 
                    color="inherit" 
                    onClick={logout}
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)'
                      }
                    }}
                  >
                    <LogoutIcon />
                  </IconButton>
                </>
              )}
            </Box>

            {/* Main Navigation Row */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center'
            }}>
              <Typography
                variant="h6"
                component={Link}
                to="/"
                sx={{
                  color: 'white',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.8)'
                  }
                }}
              >
                HOB App
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                flexWrap: 'wrap',
                justifyContent: 'flex-end'
              }}>
                {navItems.map((item) => (
                  <Button
                    key={item.text}
                    component={Link}
                    to={item.path}
                    color="inherit"
                    startIcon={item.icon}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      },
                      whiteSpace: 'nowrap',
                      minWidth: isMobile ? 'auto' : undefined,
                      px: isMobile ? 1 : 2
                    }}
                  >
                    {!isMobile && item.text}
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>
        </Container>
      </AppBar>

      <Box sx={{ 
        height: '64px', // Same as the navbar height
        width: '100%',
      }} />

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            backgroundColor: '#ffffff',
          },
        }}
      >
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Menu
          </Typography>
          <IconButton onClick={toggleDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        {renderNavItems()}
      </Drawer>

      <Dialog open={editAccountOpen} onClose={handleEditAccountClose}>
        <DialogTitle>Edit Account</DialogTitle>
        <DialogContent>
          <EditAccount onClose={handleEditAccountClose} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditAccountClose} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </>
  );
};

export default Navbar;